import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { Injectable, inject, Directive, HostListener, booleanAttribute, Component, ViewEncapsulation, ChangeDetectionStrategy, Input, Output, ViewChild, NgModule } from '@angular/core';
import { CSSComponent } from '@odx/angular/internal';
import { injectElement, createConfigTokens, buildWebsiteUrl, untilDestroyed } from '@odx/angular/utils';
import { BehaviorSubject, distinctUntilChanged, shareReplay, of, combineLatest, map, tap, fromEvent } from 'rxjs';
import * as i1$1 from '@odx/angular';
import { CoreModule, WithDisabledState, WithTabIndex } from '@odx/angular';
import { A11yModule } from '@odx/angular/cdk/a11y';
import { DynamicViewDirective } from '@odx/angular/cdk/dynamic-view';
import * as i4 from '@odx/angular/components/area-header';
import { AreaHeaderModule } from '@odx/angular/components/area-header';
import * as i5 from '@odx/angular/components/button';
import { ButtonComponent } from '@odx/angular/components/button';
import * as i6 from '@odx/angular/components/icon';
import { IconComponent } from '@odx/angular/components/icon';
import { LinkDirective } from '@odx/angular/components/link';
import { LogoDirective } from '@odx/angular/components/logo';
import { provideTranslations, TranslatePipe } from '@odx/angular/internal/translate';
import * as i1 from '@angular/common';
import * as i3 from '@angular/cdk/a11y';
import { RouterLinkActive } from '@angular/router';

/**
 * Provides control over the state of the main menu, allowing components to open, close, or toggle its visibility.
 * This service uses a BehaviorSubject to manage the open state and exposes an observable that emits the current state.
 */
class MainMenuService {
    constructor() {
        this.isOpen$$ = new BehaviorSubject(false);
        /**
         * An Observable that emits the current state of the menu's visibility.
         * It uses distinctUntilChanged to prevent unnecessary emissions when the state hasn't actually changed.
         * shareReplay is configured to cache the last value for any late subscribers.
         *
         * @emits {boolean} The current visibility state of the main menu.
         */
        this.isOpen$ = this.isOpen$$.pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));
    }
    /**
     * Returns the current visibility state of the main menu.
     *
     * @returns {boolean} True if the menu is open, false otherwise.
     */
    isOpen() {
        return this.isOpen$$.getValue();
    }
    /**
     * Opens the main menu by setting its state to true.
     */
    open() {
        this.toggle(true);
    }
    /**
     * Closes the main menu by setting its state to false.
     */
    close() {
        this.toggle(false);
    }
    /**
     * Toggles the visibility state of the main menu.
     *
     * @param {boolean | undefined} forceState - If provided, the menu will be set to this specific state. If not provided,
     * the menu will toggle to the opposite of its current state.
     */
    toggle(forceState) {
        this.isOpen$$.next(forceState ?? !this.isOpen());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MainMenuService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MainMenuService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MainMenuService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * Represents a directive for a main menu button.
 * This directive is used to toggle the main menu.
 */
let MainMenuButtonDirective = class MainMenuButtonDirective {
    constructor() {
        this.mainMenuService = inject(MainMenuService);
        this.element = injectElement();
    }
    onClick() {
        this.mainMenuService.toggle();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MainMenuButtonDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: MainMenuButtonDirective, isStandalone: true, selector: "button[odxMainMenuButton]", host: { listeners: { "click": "onClick()" } }, ngImport: i0 }); }
};
MainMenuButtonDirective = __decorate([
    CSSComponent('main-menu-button')
], MainMenuButtonDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MainMenuButtonDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'button[odxMainMenuButton]',
                    standalone: true,
                }]
        }], propDecorators: { onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

/**
 * Tokens generated to provide and inject `MainMenuLinksConfig`. These tokens are used to configure and access main menu links throughout the application.
 *
 * @exports
 * - `MainMenuLinksConfig` - Token for injecting the config.
 * - `MainMenuLinksDefaultConfig` - Default configuration for main menu links.
 * - `injectMainMenuLinksConfig` - Function to inject the config wherever needed.
 * - `provideMainMenuLinksConfig` - Provider function to configure and provide the config in Angular's DI system.
 *
 * @example
 * ```ts
 * // Providing main menu links in an Angular module
 * @NgModule({
 *  providers: [
 *   provideMainMenuLinksConfig({
 *     legalNoticeUrl: 'https://example.com/legalnotice',
 *     providerIdentificationUrl: 'https://example.com/provideridentification',
 *    }),
 *   ],
 *  })
 * export class AppModule { }
 *
 * // Injecting and using the main menu links configuration in a component
 * @Component({ ... })
 * export class MyComponent {
 *  constructor(@Inject(MainMenuLinksConfig) private config: MainMenuLinksConfig) {
 *   console.log('Legal Notice URL:', config.legalNoticeUrl);
 *  }
 * }
 * ```
 */
const { MainMenuLinksConfig, MainMenuLinksDefaultConfig, injectMainMenuLinksConfig, provideMainMenuLinksConfig } = createConfigTokens('MainMenuLinks', '@odx/angular/components/main-menu', {
    providerIdentificationUrl: buildWebsiteUrl('provideridentification'),
    legalNoticeUrl: buildWebsiteUrl('legalnotice'),
});

/** @internal */
function createMainMenuLinks$() {
    const { legalNoticeUrl, providerIdentificationUrl, dataProtectionUrl, openSourceUrl, termsAndConditionsUrl, statusUrl, dynamicLinks } = injectMainMenuLinksConfig();
    const dynamicLinks$ = dynamicLinks ?? of([]);
    const links = [
        { name: 'status', url: statusUrl },
        { name: 'termsAndConditions', url: termsAndConditionsUrl },
        { name: 'providerIdentification', url: providerIdentificationUrl },
        { name: 'openSource', url: openSourceUrl },
        { name: 'dataProtection', url: dataProtectionUrl },
        { name: 'legalNotice', url: legalNoticeUrl },
    ];
    return combineLatest([dynamicLinks$, of(links)].filter(Boolean)).pipe(map((links) => links.flat().filter((link) => !!link.url)));
}

var mainMenuTranslations = {
    en: {
        legalNotice: 'Legal notice',
        providerIdentification: 'Provider identification',
        dataProtection: 'Data protection',
        openSource: 'Open-source licenses',
        termsAndConditions: 'Terms and Conditions',
        status: 'Status',
    },
    de: {
        legalNotice: 'Rechtliche Hinweise',
        providerIdentification: 'Impressum',
        dataProtection: 'Datenschutz',
        openSource: 'Open-Source-Lizenzen',
        termsAndConditions: 'Allgemeine Geschäftsbedingungen',
        status: 'Status',
    },
};

/**
 * The `MainMenuComponent` provides a comprehensive user interface component for displaying a navigation menu within an application.
 * It supports dynamic content for titles, subtitles, and copyright text, and can dynamically render additional links.
 * The menu includes animations for opening and closing states, and can be controlled programmatically or via user interaction.
 */
let MainMenuComponent = class MainMenuComponent {
    constructor() {
        this.takeUntilDestroyed = untilDestroyed();
        this.mainMenuService = inject(MainMenuService);
        this.mainMenuLinks$ = createMainMenuLinks$();
        this.isOpen$ = this.mainMenuService.isOpen$.pipe(tap((isOpen) => this.slideMenuInner(isOpen)));
        this.element = injectElement();
        /**
         * Optional input property for the subtitle of the menu.
         *
         * @type {DynamicTextContent | null}
         * @default null
         */
        this.subtitle = null;
        /**
         * Optional input property for the copyright text of the menu.
         *
         * @type {DynamicTextContent | null}
         * @default null
         */
        this.copyright = null;
        /**
         * Outputs the observable that tracks the open state of the menu.
         *
         * @emits {boolean} The open state of the menu.
         */
        this.menuOpen = this.mainMenuService.isOpen$;
    }
    /**
     * Input property to control the open state of the menu. Uses boolean transformation to handle true/false states.
     * Changing this property will toggle the menu's open state via the MainMenuService.
     *
     * @param {boolean} value - The open state of the menu.
     */
    set isOpen(value) {
        this.mainMenuService.toggle(value);
    }
    /**
     * Returns the current open state of the menu from the MainMenuService.
     *
     * @returns {boolean} `true` if the menu is open, `false` otherwise.
     */
    get isOpen() {
        return this.mainMenuService.isOpen();
    }
    ngAfterViewInit() {
        fromEvent(this.menuInner?.nativeElement, 'toggle')
            .pipe(tap((e) => 'newState' in e && this.mainMenuService.toggle(e.newState === 'open')), this.takeUntilDestroyed())
            .subscribe();
    }
    /**
     * Closes the menu.
     */
    close() {
        this.mainMenuService.close();
    }
    slideMenuInner(isOpen = false) {
        const { nativeElement } = this.menuInner || {};
        if (!(nativeElement && this.isPopoverElement(nativeElement)))
            return;
        const method = isOpen ? 'showPopover' : 'hidePopover';
        nativeElement[method]();
    }
    isPopoverElement(element) {
        return element.hasAttribute('popover') && 'showPopover' in element && 'hidePopover' in element;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MainMenuComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: MainMenuComponent, isStandalone: true, selector: "odx-main-menu", inputs: { title: "title", subtitle: "subtitle", copyright: "copyright", isOpen: ["isOpen", "isOpen", booleanAttribute] }, outputs: { menuOpen: "menuOpen" }, host: { listeners: { "window:keyup.esc": "close()" }, properties: { "class.is-open": "isOpen" } }, providers: [provideTranslations(mainMenuTranslations)], viewQueries: [{ propertyName: "menuInner", first: true, predicate: ["menuInner"], descendants: true }], ngImport: i0, template: "<aside class=\"odx-main-menu__inner\" [ngClass]=\"(isOpen$ | async) ? 'opened' : 'closed'\" #menuInner popover=\"auto\" cdkTrapFocus cdkTrapFocusAutoCapture>\n  <div class=\"odx-main-menu__actions\">\n    <button class=\"odx-main-menu__close\" odxButton disabled=\"false\" (click)=\"close()\"><odx-icon name=\"close\" iconSet=\"core\" /></button>\n  </div>\n  <odx-area-header class=\"odx-main-menu__header\" size=\"large\">\n    <ng-template [odxDynamicView]=\"title\" />\n    @if (subtitle) {\n      <odx-area-header-subtitle>\n        <ng-template [odxDynamicView]=\"subtitle\" />\n      </odx-area-header-subtitle>\n    }\n  </odx-area-header>\n  <nav class=\"odx-main-menu__content\">\n    <ng-content />\n  </nav>\n  <div class=\"odx-main-menu__info\">\n    <ng-content select=\"[odxLink]\" />\n    @for (link of mainMenuLinks$ | async; track $index) {\n      <a odxLink [href]=\"link.url\" target=\"_blank\">\n        {{ link.name | odxTranslate | async }}\n      </a>\n    }\n  </div>\n  <div class=\"odx-main-menu__footer\">\n    <odx-logo variant=\"inverse\" />\n    @if (copyright) {\n      <p class=\"odx-text odx-text--small\">\n        <ng-template [odxDynamicView]=\"copyright\" />\n      </p>\n    }\n  </div>\n</aside>\n", dependencies: [{ kind: "ngmodule", type: A11yModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "directive", type: i1$1.DisabledController, selector: "[disabled]", inputs: ["disabled"] }, { kind: "directive", type: i3.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }, { kind: "ngmodule", type: CoreModule }, { kind: "ngmodule", type: AreaHeaderModule }, { kind: "component", type: i4.AreaHeaderComponent, selector: "odx-area-header", inputs: ["size"] }, { kind: "directive", type: i4.AreaHeaderSubtitleDirective, selector: "odx-area-header-subtitle" }, { kind: "component", type: i5.ButtonComponent, selector: "button[odxButton], a[odxButton]", inputs: ["variant", "size"] }, { kind: "component", type: i6.IconComponent, selector: "odx-icon", inputs: ["inline", "size", "name", "iconSet", "identifier"] }, { kind: "directive", type: LogoDirective, selector: "odx-logo", inputs: ["size", "variant"] }, { kind: "pipe", type: TranslatePipe, name: "odxTranslate" }, { kind: "directive", type: LinkDirective, selector: "a[odxLink]" }, { kind: "directive", type: DynamicViewDirective, selector: "ng-template[odxDynamicView]", inputs: ["odxDynamicView", "odxDynamicViewInjector", "odxDynamicViewContext"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
MainMenuComponent = __decorate([
    CSSComponent('main-menu')
], MainMenuComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MainMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'odx-main-menu', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, imports: [A11yModule, CoreModule, AreaHeaderModule, ButtonComponent, IconComponent, LogoDirective, TranslatePipe, LinkDirective, DynamicViewDirective], providers: [provideTranslations(mainMenuTranslations)], standalone: true, host: { '[class.is-open]': 'isOpen' }, template: "<aside class=\"odx-main-menu__inner\" [ngClass]=\"(isOpen$ | async) ? 'opened' : 'closed'\" #menuInner popover=\"auto\" cdkTrapFocus cdkTrapFocusAutoCapture>\n  <div class=\"odx-main-menu__actions\">\n    <button class=\"odx-main-menu__close\" odxButton disabled=\"false\" (click)=\"close()\"><odx-icon name=\"close\" iconSet=\"core\" /></button>\n  </div>\n  <odx-area-header class=\"odx-main-menu__header\" size=\"large\">\n    <ng-template [odxDynamicView]=\"title\" />\n    @if (subtitle) {\n      <odx-area-header-subtitle>\n        <ng-template [odxDynamicView]=\"subtitle\" />\n      </odx-area-header-subtitle>\n    }\n  </odx-area-header>\n  <nav class=\"odx-main-menu__content\">\n    <ng-content />\n  </nav>\n  <div class=\"odx-main-menu__info\">\n    <ng-content select=\"[odxLink]\" />\n    @for (link of mainMenuLinks$ | async; track $index) {\n      <a odxLink [href]=\"link.url\" target=\"_blank\">\n        {{ link.name | odxTranslate | async }}\n      </a>\n    }\n  </div>\n  <div class=\"odx-main-menu__footer\">\n    <odx-logo variant=\"inverse\" />\n    @if (copyright) {\n      <p class=\"odx-text odx-text--small\">\n        <ng-template [odxDynamicView]=\"copyright\" />\n      </p>\n    }\n  </div>\n</aside>\n" }]
        }], propDecorators: { title: [{
                type: Input,
                args: [{ required: true }]
            }], subtitle: [{
                type: Input
            }], copyright: [{
                type: Input
            }], isOpen: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], menuOpen: [{
                type: Output
            }], menuInner: [{
                type: ViewChild,
                args: ['menuInner']
            }], close: [{
                type: HostListener,
                args: ['window:keyup.esc']
            }] } });

/**
 * Represents a directive for closing the main menu.
 */
class MainMenuCloseDirective {
    constructor() {
        this.mainMenu = inject(MainMenuComponent, { optional: true });
    }
    onClick() {
        this.mainMenu?.close();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MainMenuCloseDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: MainMenuCloseDirective, isStandalone: true, selector: "[odxMainMenuClose]", host: { listeners: { "click": "onClick()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MainMenuCloseDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: '[odxMainMenuClose]',
                }]
        }], propDecorators: { onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

/**
 * Represents a directive for a main menu item.
 * This directive is used to define a main menu item.
 * Has host directives for disabled state, tabindex, and closing the main menu.
 *
 * @see {WithDisabledState}
 * @see {WithTabIndex}
 * @see {MainMenuCloseDirective}
 */
let MainMenuItemDirective = class MainMenuItemDirective {
    constructor() {
        this.routerLinkActive = inject(RouterLinkActive, { optional: true });
        this.element = injectElement();
        /**
         * Optionally specifies whether the menu item is selected.
         * If not specified, the menu item will not be selected.
         *
         * @type {boolean}
         * @default false
         */
        this.isSelected = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MainMenuItemDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "16.1.0", version: "18.2.13", type: MainMenuItemDirective, isStandalone: true, selector: "[odxMainMenuItem]", inputs: { isSelected: ["isSelected", "isSelected", booleanAttribute] }, host: { properties: { "class.is-selected": "isSelected || routerLinkActive?.isActive", "attr.role": "\"button\"" } }, hostDirectives: [{ directive: i1$1.WithDisabledState }, { directive: i1$1.WithTabIndex }, { directive: MainMenuCloseDirective }], ngImport: i0 }); }
};
MainMenuItemDirective = __decorate([
    CSSComponent('main-menu-item')
], MainMenuItemDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MainMenuItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[odxMainMenuItem]',
                    standalone: true,
                    hostDirectives: [WithDisabledState, WithTabIndex, MainMenuCloseDirective],
                    host: {
                        '[class.is-selected]': 'isSelected || routerLinkActive?.isActive',
                        '[attr.role]': '"button"',
                    },
                }]
        }], propDecorators: { isSelected: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }] } });

const modules = [LinkDirective, MainMenuComponent, MainMenuItemDirective, MainMenuButtonDirective, MainMenuCloseDirective];
class MainMenuModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MainMenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: MainMenuModule, imports: [LinkDirective, MainMenuComponent, MainMenuItemDirective, MainMenuButtonDirective, MainMenuCloseDirective], exports: [CoreModule, LinkDirective, MainMenuComponent, MainMenuItemDirective, MainMenuButtonDirective, MainMenuCloseDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MainMenuModule, imports: [MainMenuComponent, CoreModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MainMenuModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: modules,
                    exports: [CoreModule, ...modules],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { MainMenuButtonDirective, MainMenuCloseDirective, MainMenuComponent, MainMenuItemDirective, MainMenuLinksConfig, MainMenuLinksDefaultConfig, MainMenuModule, injectMainMenuLinksConfig, provideMainMenuLinksConfig };
